import { Download, Minus, Plus, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import type { PaymentAttachment } from '../types';
import { PdfAttachmentPreview } from './PdfAttachmentPreview';
import { isMissingCloudFile } from '../lib/cloudFileState';
import { useCloudFile } from '../lib/useCloudFile';

interface AttachmentPreviewProps {
  attachment: PaymentAttachment;
  label: string;
  onClose: () => void;
}

export function AttachmentPreview({ attachment, label, onClose }: AttachmentPreviewProps) {
  const [scale, setScale] = useState(1);
  const file = useCloudFile(attachment.dataUrl);

  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [onClose]);

  const missing = isMissingCloudFile(attachment.dataUrl);
  const isImage = !missing && Boolean(file.url) && attachment.type.startsWith('image/');
  const isPdf = attachment.type === 'application/pdf';

  return <div className="attachment-preview-backdrop" role="presentation" onMouseDown={onClose}>
    <section className="attachment-preview-modal" role="dialog" aria-modal="true" aria-label={`${label} ${attachment.name}`} onMouseDown={(event) => event.stopPropagation()}>
      <header>
        <div><span className="eyebrow">{label}</span><strong title={attachment.name}>{attachment.name}</strong></div>
        <div className="attachment-preview-actions">
          {isImage && <><button className="icon-btn" type="button" onClick={() => setScale((value) => Math.max(.5, value - .25))} aria-label="ย่อภาพ" disabled={scale <= .5}><Minus size={18} /></button><span>{Math.round(scale * 100)}%</span><button className="icon-btn" type="button" onClick={() => setScale((value) => Math.min(3, value + .25))} aria-label="ขยายภาพ" disabled={scale >= 3}><Plus size={18} /></button></>}
          {!missing && file.url && <a className="btn ghost small" href={file.url} download={attachment.name}><Download size={15} /> ดาวน์โหลด</a>}
          <button className="icon-btn" type="button" onClick={onClose} aria-label="ปิด"><X size={20} /></button>
        </div>
      </header>
      <div className="attachment-preview-body">
        {file.loading ? <p role="status">กำลังโหลดไฟล์จาก Cloud…</p> : file.error ? <p role="alert">{file.error}</p> : missing ? <div className="attachment-unsupported" role="alert"><strong>ไฟล์แนบสูญหาย</strong><p>ไม่พบต้นฉบับบน Cloud และในเครื่องนี้ ข้อมูลเอกสาร ชื่อไฟล์ และคีย์อ้างอิงยังอยู่ แต่ยังเปิดหรือดาวน์โหลดไฟล์นี้ไม่ได้ กรุณาหาต้นฉบับจากเครื่องอื่นหรือไฟล์สำรอง แล้วซิงค์ใหม่</p></div> : isPdf
          ? <PdfAttachmentPreview dataUrl={file.url} name={attachment.name} />
          : isImage
            ? <div className="attachment-image-stage"><img src={file.url} alt={`${label} ${attachment.name}`} style={{ transform: `scale(${scale})` }} /></div>
            : <div className="attachment-unsupported"><strong>ไม่สามารถแสดงตัวอย่างไฟล์ประเภทนี้ได้</strong><a className="btn primary small" href={file.url} download={attachment.name}><Download size={15} /> ดาวน์โหลดไฟล์</a></div>}
      </div>
    </section>
  </div>;
}
